// Contact page: interactive map + opening hours side by side, above the contact form.
import React from 'react';
import { MapPin, Clock, Navigation } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import { getHours, buildHourRows } from '../i18n/intros';

const MAP_EMBED = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1491.2!2d19.2342801!3d41.9100076!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x134e1500ed0c545b%3A0xea28060ed2fa2b9f!2sHotel%20La%20Rosa%20Negra!5e0!3m2!1sen!2sme!4v1707753600000!5m2!1sen!2sme';

const ContactMapHours = () => {
  const { hotelInfo } = useHotel();
  const { lang } = useLanguage();
  const h = getHours(lang);

  const rows = buildHourRows(lang, hotelInfo.hours_active || hotelInfo.hours).map((r, i) => (
    <li key={r.key} className="flex items-start justify-between gap-4 py-2.5 border-b border-white/10 last:border-0" data-testid={`contact-hours-row-${i}`}>
      <span className="text-gray-300">{r.label}</span>
      <span className="font-semibold text-white text-right whitespace-nowrap">{r.value}</span>
    </li>
  ));

  return (
    <section className="bg-white py-12 sm:py-16" aria-labelledby="contact-map-title" data-testid="contact-map-hours">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8 items-stretch">
          <div className="lg:col-span-3 rounded-2xl overflow-hidden shadow-xl border border-gray-100 min-h-[320px]">
            <iframe
              title="Hotel La Rosa Negra on Google Maps"
              src={MAP_EMBED}
              width="100%"
              height="100%"
              className="w-full h-full min-h-[320px] lg:min-h-[420px]"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
            />
          </div>

          <div className="lg:col-span-2 bg-[#0f1318] text-white rounded-2xl p-6 sm:p-8 shadow-xl flex flex-col">
            <h2 id="contact-map-title" className="text-lg md:text-xl font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-400" /> {h.title}
            </h2>
            <ul className="mt-4 text-sm" data-testid="contact-hours-list">{rows}</ul>
            {hotelInfo.hours_active?.season && (
              <p className="mt-2 text-[11px] uppercase tracking-wider text-orange-300 font-semibold" data-testid="contact-hours-season">{hotelInfo.hours_active.season}</p>
            )}
            <p className="mt-4 text-xs text-gray-400" data-testid="contact-hours-note">{h.note}</p>

            <div className="mt-auto pt-6 border-t border-white/10 text-sm">
              <p className="flex items-start gap-2 text-gray-300" data-testid="contact-address">
                <MapPin className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>{hotelInfo.address}, {hotelInfo.postalCode} {hotelInfo.city}, {hotelInfo.country}</span>
              </p>
              <a
                href={hotelInfo.mapUrl || 'https://maps.app.goo.gl/'}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 transition-colors"
                data-testid="contact-directions-link"
              >
                <Navigation className="w-4 h-4" /> {h.directions}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactMapHours;
